// src/vlp/components/common/TestCodeSelectorDialog.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  CircularProgress,
  Typography,
  Box,
  Chip,
  InputAdornment,
  Divider,
  Paper,
  Avatar,
} from "@mui/material";
import {
  Search as SearchIcon,
  Science as ScienceIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTestLib,
  selectTestLibData,
  selectTestLibStatus,
  selectTestLibError,
} from "../../../redux/testLib/testLibSlice"; // adjust path

const TestCodeSelectorDialog = ({
  open,
  onClose,
  onSelect,
  multiSelect = false,
}) => {
  const dispatch = useDispatch();
  const testsFromStore = useSelector(selectTestLibData);
  const testsStatus = useSelector(selectTestLibStatus);
  const testsError = useSelector(selectTestLibError);

  const [search, setSearch] = useState("");
  const [selectedCodes, setSelectedCodes] = useState([]);
  const searchInputRef = useRef(null);
  // Normalize - Updated for TestlibSearchDto structure
  const getTestCode = (t) => t?.testCode ?? "";
  const getTestParameter = (t) => t?.parameter ?? "";
  const getMethodCode = (t) => t?.methodCode ?? "";
  const getTestUnits = (t) => t?.testUnits ?? "";
  const getCostOfAnalysis = (t) => t?.costOfAnalysis ?? null;

  // Load data
  useEffect(() => {
    if (open && testsStatus === "idle") {
      dispatch(fetchTestLib());
    }
  }, [open, testsStatus, dispatch]);

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedCodes([]);
      setSearch("");
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Filter + limit to 20
  const filteredTests = useMemo(() => {
    let list = testsFromStore || [];
    if (search.trim()) {
      const term = search.toLowerCase();
      list = list.filter((t) => {
        const testCode = t?.testCode?.toLowerCase() || "";
        const parameter = t?.parameter?.toLowerCase() || "";
        const methodCode = t?.methodCode?.toLowerCase() || "";
        const testUnits = t?.testUnits?.toLowerCase() || "";
        const analyteCode = t?.analyteCode?.toLowerCase() || "";
        const sampleBottleName = t?.sampleBottleName?.toLowerCase() || "";
        const bottlePreservatives = t?.bottlePreservatives?.toLowerCase() || "";
        const cocNumber = t?.cocNumber?.toLowerCase() || "";
        const bottleqty = t?.bottleqty?.toString() || "";

        return (
          testCode.includes(term) ||
          parameter.includes(term) ||
          methodCode.includes(term) ||
          testUnits.includes(term) ||
          analyteCode.includes(term) ||
          sampleBottleName.includes(term) ||
          bottlePreservatives.includes(term) ||
          cocNumber.includes(term) ||
          bottleqty.includes(term)
        );
      });
    }
    return list.slice(0, 20);
  }, [testsFromStore, search]);

  const toggleSelection = (code) => {
    if (!multiSelect) {
      setSelectedCodes([code]);
      // Auto-select and close for single select
      const test = (testsFromStore || []).find((t) => getTestCode(t) === code);
      onSelect(test || null);
      onClose();
      return;
    }
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleConfirm = () => {
    if (multiSelect) {
      const selectedTests = (testsFromStore || []).filter((t) =>
        selectedCodes.includes(getTestCode(t))
      );
      onSelect(selectedTests);
    } else {
      const test = (testsFromStore || []).find(
        (t) => getTestCode(t) === selectedCodes[0]
      );
      onSelect(test || null);
    }
    onClose();
  };

  const removeSelectedCode = (codeToRemove) => {
    setSelectedCodes((prev) => prev.filter((code) => code !== codeToRemove));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "60vh",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <ScienceIcon />
        {multiSelect ? "Select Test Codes" : "Select Test Code"}
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {testsError && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              bgcolor: "error.lighter",
              border: "1px solid",
              borderColor: "error.light",
              borderRadius: 2,
            }}
          >
            <Typography color="error" variant="body2">
              {testsError}
            </Typography>
          </Paper>
        )}

        <TextField
          inputRef={searchInputRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by test code, parameter, method, or units..."
          fullWidth
          variant="outlined"
          size="small"
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: "grey.50",
              height: "40px",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* Selected items chips for multiSelect */}
        {multiSelect && selectedCodes.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Selected ({selectedCodes.length}):
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {selectedCodes.map((code) => (
                <Chip
                  key={code}
                  label={code}
                  onDelete={() => removeSelectedCode(code)}
                  deleteIcon={<CloseIcon />}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        {testsStatus === "loading" ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress size={40} />
              <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                Loading test codes...
              </Typography>
            </Box>
          </Box>
        ) : filteredTests.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              color: "text.secondary",
            }}
          >
            <SearchIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6">No results found</Typography>
            <Typography variant="body2">
              Try adjusting your search terms
            </Typography>
          </Box>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              maxHeight: 400,
              overflowY: "auto",
            }}
          >
            <List dense sx={{ p: 0 }}>
              {filteredTests.map((t, index) => {
                const code = getTestCode(t);
                const parameter = getTestParameter(t);
                const isSelected = selectedCodes.includes(code);

                return (
                  <React.Fragment key={code}>
                    <ListItem
                      disablePadding
                      sx={{
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <ListItemButton
                        onClick={() => toggleSelection(code)}
                        sx={{
                          py: 1,
                          px: 2,
                          minHeight: "48px",
                          ...(isSelected && {
                            bgcolor: "primary.lighter",
                            "&:hover": {
                              bgcolor: "primary.light",
                            },
                          }),
                        }}
                      >
                        {multiSelect && (
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Checkbox
                              edge="start"
                              checked={isSelected}
                              tabIndex={-1}
                              disableRipple
                              color="primary"
                              size="small"
                            />
                          </ListItemIcon>
                        )}
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            mr: 2,
                            bgcolor: isSelected ? "primary.main" : "grey.300",
                            fontSize: "0.7rem",
                          }}
                        >
                          {code.substring(0, 2).toUpperCase()}
                        </Avatar>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: isSelected ? 600 : 400,
                                  color: isSelected
                                    ? "primary.main"
                                    : "text.primary",
                                }}
                              >
                                {code}
                              </Typography>
                              {getMethodCode(t) && (
                                <Chip
                                  label={getMethodCode(t)}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: "0.7rem" }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              {parameter && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 0.5 }}
                                >
                                  {parameter}
                                </Typography>
                              )}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                }}
                              >
                                {getTestUnits(t) && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Units: {getTestUnits(t)}
                                  </Typography>
                                )}
                                {getCostOfAnalysis(t) && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Cost: ${getCostOfAnalysis(t)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < filteredTests.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        )}

        {filteredTests.length === 20 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block", textAlign: "center" }}
          >
            Showing first 20 results. Use search to narrow down.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: "grey.50" }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        {multiSelect && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={selectedCodes.length === 0}
            sx={{
              borderRadius: 2,
              px: 3,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
            }}
          >
            Select ({selectedCodes.length})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TestCodeSelectorDialog;
